import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import { makeStyles } from 'tss-react/mui';

import { useL10n } from '@uplift/hooks';
import { ROUTING_CONFIG } from '@uplift/constants';
import { betterhelpSsoLinkConfirm } from '@uplift/api';
import { AsyncButton, Button, Loader } from '@uplift/ui-v2';
import { asyncWait } from '@uplift/utils';


type LandingState =
  | { kind: 'loading' }
  | { kind: 'login'; code: string; redirectTo: string }
  | { kind: 'link_pending'; intent: string; redirectTo: string }
  | { kind: 'error' };


// redirect_to is already host-allowlisted by the backend before it reaches the
// landing page; the FE only rejects dangerous schemes (javascript:, data:, ...).
const safeRedirect = (raw: string | null): string | null => {
  if (!raw) return null;
  try {
    const url = new URL(raw, window.location.origin);

    return url.protocol === 'https:' || url.protocol === 'http:' ? raw : null;
  } catch {
    return null;
  }
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    textAlign: 'center',
    flex: 1,
  },
  panel: {
    maxWidth: '432px',
    width: '100%',
    textAlign: 'center',
  },
  errorIcon: {
    fontSize: '48px',
    color: theme.palette.grey[700],
    marginBottom: theme.spacing(2),
  },
  actions: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  signingInText: {
    marginTop: theme.spacing(3),
  },
  panelTitle: {
    marginBottom: theme.spacing(2),
  },
}));


const LinkPendingFlow: React.VFC<{
  intent: string;
  redirectTo: string;
  setErrored: () => void;
  loginWithCode: (redirectTo: string, code: string) => void;
}> = ({ intent, redirectTo, setErrored, loginWithCode }) => {
  const history = useHistory();
  const l10n = useL10n();
  const { classes } = useStyles();

  const handleConfirmLink = async (intent: string, redirectTo: string) => {
    try {
      const [ { data } ] = await Promise.all([
        betterhelpSsoLinkConfirm(intent),
        asyncWait(1000)
      ]);

      loginWithCode(redirectTo, data.code);
    } catch {
      setErrored();
    }
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.panel}>
        <Typography className={classes.panelTitle} variant="h2">{l10n.auth.betterhelpSso.linkTitle}</Typography>
        <Typography variant="body1">{l10n.auth.betterhelpSso.linkBody}</Typography>
        <Box className={classes.actions}>
          <AsyncButton
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            onClick={async () => handleConfirmLink(intent, redirectTo)}
          >
            {l10n.auth.betterhelpSso.confirm}
          </AsyncButton>
          <Button variant="text" color="secondary" onClick={() => history.push('/')}>
            {l10n.auth.betterhelpSso.decline}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const LoginFlow: React.VFC<{
  code: string;
  redirectTo: string;
  loginWithCode: (redirectTo: string, code: string) => void;
}> = ({ redirectTo, code, loginWithCode }) => {
  const l10n = useL10n();
  const { classes } = useStyles();

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      loginWithCode(redirectTo, code);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [ redirectTo, code, loginWithCode ]);

  return (
    <Box className={classes.root}>
      <Loader color="primary" />
      <Typography className={classes.signingInText} variant="body1">{l10n.auth.betterhelpSso.signingIn}</Typography>
    </Box>
  );
};


export const BetterhelpSsoLandingPage = () => {
  const { classes } = useStyles();
  const l10n = useL10n();
  const history = useHistory();
  const location = useLocation();
  const params = useMemo(() => new URLSearchParams(location.search), [ location.search ]);
  const [ _state, setState ] = useState<LandingState | null>(null);
  const setErrored = () => setState({ kind: 'error' });

  const loginWithCode = (redirectTo: string, code: string) => {
    const safe = safeRedirect(redirectTo);

    if (safe) {
      window.location.replace(`${redirectTo}?flow=betterhelp&code=${code}`);
    } else {
      history.replace(ROUTING_CONFIG.home);
    }
  };

  const state: LandingState = React.useMemo(() => {
    if (_state) return _state;

    if (params.get('error')) {
      return { kind: 'error' };
    }

    const outcome = params.get('outcome');
    const redirectTo = params.get('redirect_to');
    const code = params.get('code');
    const intent = params.get('intent');

    if (outcome === 'signed_in' && code && redirectTo) {
      return {
        kind: 'login',
        code: code,
        redirectTo: redirectTo,
      };
    }
    if (outcome === 'link_pending' && intent && redirectTo) {
      return {
        kind: 'link_pending',
        redirectTo,
        intent,
      };
    }

    return { kind: 'error' };
  }, [ params, _state ]);

  if (state.kind === 'error') {
    return (
      <Box className={classes.root}>
        <Box className={classes.panel}>
          <ErrorOutline className={classes.errorIcon} />
          <Typography className={classes.panelTitle} variant="h2">{l10n.auth.betterhelpSso.errorTitle}</Typography>
          <Typography className={classes.panelTitle} variant="body1" color="text.secondary">{l10n.auth.betterhelpSso.errorBody}</Typography>
          <Typography variant="body1" color="text.secondary">{l10n.auth.betterhelpSso.errorContact}</Typography>
          <Box className={classes.actions}>
            <Button
              fullWidth variant="outlined" color="primary" size="large"
              onClick={() => {
                history.push('/');
              }}
            >
              {l10n.auth.betterhelpSso.backToLogin}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  if (state.kind === 'link_pending') {
    return (
      <LinkPendingFlow
        intent={state.intent}
        redirectTo={state.redirectTo}
        setErrored={setErrored}
        loginWithCode={loginWithCode}
      />
    );
  }

  if (state.kind === 'login') {
    return (
      <LoginFlow
        code={state.code}
        redirectTo={state.redirectTo}
        loginWithCode={loginWithCode}
      />
    );
  }

  return (
    <Box className={classes.root}>
      <Loader color="primary" />
      <Typography className={classes.signingInText} variant="body1">{l10n.auth.betterhelpSso.signingIn}</Typography>
    </Box>
  );


};
